import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ThemeProvider, Box, Typography, Divider
} from '@mui/material';
import { theme } from '../../Medical_History_Forms/themeprovider';
import Grid from '@mui/material/Grid2';
import {
  infoTextStyle, sectionTitleStyle
} from '../../../utils/commonUtils';
import { getLaparoscopy, LaparoscopyType } from './laparoscopy.helper';
import { useLoader } from '../../loaderProvider/loader';
import styles from '../oocyteRetrieval/oocyteRetrival.module.css';
import { DocumentChips, FileViewerModal, UploadedImageType } from '../../common/ImageUploadModal';

interface LaparoscopyViewDetailsProps {
  record: number;
  onBack: () => void;
  onEdit?: () => void;
  canEditOrAdd?: boolean;
  canView?: boolean;
}

const LaparoscopyViewDetails: React.FC<LaparoscopyViewDetailsProps> = ({ record, onBack, onEdit, canEditOrAdd, canView   }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  const { showLoader, hideLoader } = useLoader();
  const [retrievalData, setRetrievalData] = useState<LaparoscopyType | null>(null);
  const [openViewer, setOpenViewer] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);

  useEffect(() => {
    if (!record) return;

    showLoader();
    getLaparoscopy(record)
      .then((data) => setRetrievalData(data))
      .catch(() => setRetrievalData(null))
      .finally(() => hideLoader());
  }, [record, showLoader, hideLoader]);

  // Convert Files[] to UploadedImageType[] for DocumentChips
  const uploadedFiles: UploadedImageType[] = (retrievalData?.images || []).map((img, index) => {
    // Use file_name from API if available, otherwise extract from URL
    let fileName = img.file_name || `Document_${index + 1}`;
    if (!img.file_name && img.image) {
      try {
        const urlPath = new URL(img.image).pathname;
        const extractedName = urlPath.split('/').pop();
        if (extractedName && extractedName.length > 0) {
          fileName = decodeURIComponent(extractedName);
        }
      } catch {
        // Keep default name if URL parsing fails
      }
    }
    return {
      name: fileName,
      image: img.image || '',
      id: img.id,
      size: img.file_size ? (img.file_size / (1024 * 1024)).toFixed(2) : undefined,
      sizeBytes: img.file_size,
      file_extension: img.file_extension,
      isExisting: true,
    };
  });

  const handleViewFile = (index: number) => {
    setViewerInitialIndex(index);
    setOpenViewer(true);
  };
  
  const renderSection = (label: string, value: string | number | null  | undefined) => {
    if (!value) return null;
    return (
      <Grid size={3} sx={{ textAlign: 'left' }}>
        <Typography variant="body1" sx={sectionTitleStyle}>{label}</Typography>
        <Typography variant="body2" sx={infoTextStyle}>{value}</Typography>
      </Grid>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4} mb={3}>
          {renderSection(tGeneral('date'), retrievalData?.date)}
          {renderSection(tSurgery('indication'), retrievalData?.indication_label)}
          {renderSection(tSurgery('cycleDay'), `${retrievalData?.cycle_day || '-'} ${tGeneral('day')}`)}
          {renderSection(tSurgery('procedureDurationMin'), retrievalData?.procedure_duration)}
        </Grid>

        <Divider sx={{ borderColor: '#666', my: 2 }} />

        {/* Anesthesia Section */}
        {(retrievalData?.anesthesia_type || retrievalData?.anesthesia_details_remark || retrievalData?.preanesthetic_checkup) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>{tSurgery('anesthesiaDetails')}</Typography>
            </Box>
            <Grid container spacing={4} mt={2} mb={3}>
              {renderSection(tSurgery('anesthesiaType'), retrievalData?.anesthesia_type_label)}
              {renderSection(tSurgery('anesthesiaDetailsRemark'), retrievalData?.anesthesia_details_remark)}
              {renderSection(tSurgery('preanestheticCheckup'), retrievalData?.preanesthetic_checkup)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}

        {/* Patency Of Fallopian Tube */}
        {(retrievalData?.right_tube_status || retrievalData?.left_tube_status) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>{tSurgery('patencyOfFallopianTube')}</Typography>
            </Box>
            <Grid container spacing={4} mt={1} mb={3}>
              {renderSection(tSurgery('rightTube'), retrievalData?.right_tube_status)}
              {renderSection(tSurgery('leftTube'), retrievalData?.left_tube_status)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}

        {/* Right Ovary */}
        {(retrievalData?.right_ovary_status || retrievalData?.right_ovary_details) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>{tSurgery('rightOvary')}</Typography>
            </Box>
            <Grid container spacing={4} mt={1} mb={3}>
              {renderSection(tSurgery('status'), retrievalData?.right_ovary_status)}
              {renderSection(tSurgery('details'), retrievalData?.right_ovary_details)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}

        {/* Left Ovary */}
        {(retrievalData?.left_ovary_status || retrievalData?.left_ovary_details) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>{tSurgery('leftOvary')}</Typography>
            </Box>
            <Grid container spacing={4} mt={1} mb={3}>
              {renderSection(tSurgery('status'), retrievalData?.left_ovary_status)}
              {renderSection(tSurgery('otherDetails'), retrievalData?.left_ovary_details)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}

        {/* Other Details */}
        {(retrievalData?.adhesion_site || retrievalData?.adhesion_grade || retrievalData?.remark ||
          retrievalData?.endometriosis_grade || retrievalData?.uterus_status || retrievalData?.uterus_size ||
          retrievalData?.uterus_shape || retrievalData?.uterus_position || retrievalData?.uterus_mobility ||
          retrievalData?.associated_procedures || retrievalData?.eb_hpe_remark || retrievalData?.done_by_name ||
          retrievalData?.associated_doctor_name) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>{tSurgery('otherDetailsSection')}</Typography>
            </Box>
            <Grid container spacing={4} mt={1} mb={3}>
              {renderSection(tSurgery('site'), retrievalData?.adhesion_site_label)}
              {renderSection(tSurgery('adhesion'), retrievalData?.adhesion_grade_label)}
              {renderSection(tGeneral('remark'), retrievalData?.remark)}
              {renderSection(tSurgery('endometriosisGrade'), retrievalData?.endometriosis_grade_label)}
              {renderSection(tSurgery('uterusType'), retrievalData?.uterus_status_label)}
              {renderSection(tSurgery('size'), retrievalData?.uterus_size_label)}
              {renderSection(tSurgery('shape'), retrievalData?.uterus_shape_label)}
              {renderSection(tSurgery('position'), retrievalData?.uterus_position_label)}
              {renderSection(tSurgery('mobility'), retrievalData?.uterus_mobility_label)}
              {renderSection(tSurgery('associatedProcedure'), retrievalData?.associated_procedures_label)}
              {renderSection(tGeneral('remark'), retrievalData?.eb_hpe_remark)}
              {renderSection(tSurgery('doneBy'), retrievalData?.done_by_name)}
              {renderSection(tSurgery('associatedDoctor'), retrievalData?.associated_doctor_name)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}


        {/* Document Chips for uploaded files */}
        {uploadedFiles.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" sx={{ ...sectionTitleStyle, mb: 1 }}>
              Documents
            </Typography>
            <DocumentChips
              files={uploadedFiles}
              onViewClick={handleViewFile}
            />
          </Box>
        )}

        {/* File Viewer Modal */}
        <FileViewerModal
          open={openViewer}
          onClose={() => setOpenViewer(false)}
          files={uploadedFiles}
          initialIndex={viewerInitialIndex}
          title={tGeneral('preview')}
        />

        <Box className={styles.customButtonDiv}>
          <button type="button" className={styles.resetButton} onClick={onBack}>{tGeneral('backToList')}</button>

          {canEditOrAdd && canView && (
            <button type="button" className={styles.customButton} onClick={onEdit}>
              <span style={{ fontWeight: 'bold' }}>{tGeneral('edit')}</span>
            </button>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LaparoscopyViewDetails; 
